import React from "react";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import { classDates, classSpan, formatDay, formatRange, parseDate } from "./plannerUtils";
import { colorFor } from "./plannerConstants";

const hoursOf = (sc) =>
  (Array.isArray(sc.scheduledDates) ? sc.scheduledDates : []).reduce((sum, s) => {
    if (!s.startTime || !s.endTime) return sum;
    const [sh, sm] = s.startTime.split(":").map(Number);
    const [eh, em] = s.endTime.split(":").map(Number);
    return sum + (eh + em / 60 - (sh + sm / 60));
  }, 0);

/** Read-only detail panel; session edits go through the existing dialog. */
export default function ClassInspector({
  sc,
  classes,
  conflicts,
  instructorName,
  canEdit,
  onClose,
  onEditSessions,
  onRemove,
}) {
  const span = classSpan(sc);
  const mine = conflicts.filter((c) => c.a === sc.id || c.b === sc.id);
  const other = (c) => {
    const o = classes.find((x) => x.id === (c.a === sc.id ? c.b : c.a));
    const s = o && classSpan(o);
    return o ? `${o.title}${s ? `, ${formatRange(s.start, s.end)}` : ""}` : "";
  };
  const scheduled = hoursOf(sc);
  const total = sc.totalHours || 0;

  return (
    <div className="sp-dt" style={{ "--c": colorFor(sc.category) }} data-testid="planner-inspector">
      <div className="sp-dt-h">
        <span className="sp-cat">
          <i />
          {sc.category || "Other"}
        </span>
        <button type="button" className="sp-pib" onClick={onClose} aria-label="Close details">
          <CloseIcon sx={{ fontSize: 16 }} />
        </button>
      </div>
      <div className="sp-dt-t">
        <h3>{sc.title}</h3>
        {span && <span className="sp-mo">{formatRange(span.start, span.end)}, {parseDate(span.start).getFullYear()}</span>}
      </div>
      {mine.map((c, i) => (
        <div key={i} className={`sp-al ${c.blocking ? "bad" : "warn"}`}>
          <WarningAmberIcon sx={{ fontSize: 15 }} />
          <div>
            <b>{c.kind === "instructor" ? "Instructor double-booked" : "Room possibly double-booked"}</b>
            <span>
              {c.kind === "instructor" ? instructorName || "Instructor" : c.what} is also assigned to {other(c)}.
            </span>
          </div>
        </div>
      ))}
      {!sc.instructorId && (
        <div className="sp-al warn">
          <PersonOutlineIcon sx={{ fontSize: 15 }} />
          <div>
            <b>No instructor assigned</b>
            <span>Assign an instructor before students enroll.</span>
          </div>
        </div>
      )}
      <dl className="sp-facts">
        <div><dt>Instructor</dt><dd>{instructorName || "Unassigned"}</dd></div>
        <div><dt>Location</dt><dd>{sc.location || "Not set"}</dd></div>
        <div><dt>Seats</dt><dd>{sc.capacity ?? "—"}</dd></div>
        <div>
          <dt>Hours</dt>
          <dd>{scheduled.toFixed(1)}{total ? ` of ${total}` : ""} scheduled</dd>
        </div>
      </dl>
      <div className="sp-rs">
        <div className="sp-rs-t">Sessions <em>{classDates(sc).length}</em></div>
        <ul className="sp-sess">
          {(Array.isArray(sc.scheduledDates) ? sc.scheduledDates : [])
            .filter((s) => s.date)
            .slice()
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((s, i) => (
              <li key={i}>
                <span>{formatDay(s.date, { weekday: "short", month: "short", day: "numeric" })}</span>
                <span className="sp-mo">{s.startTime && s.endTime ? `${s.startTime}–${s.endTime}` : "All day"}</span>
              </li>
            ))}
        </ul>
      </div>
      {canEdit && (
        <div className="sp-dt-a">
          <button type="button" className="sp-go" onClick={onEditSessions}>
            <EditCalendarIcon sx={{ fontSize: 14 }} />
            Edit sessions
          </button>
          <button type="button" className="sp-go dg" onClick={onRemove}>
            <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            Remove
          </button>
        </div>
      )}
    </div>
  );
}
